import { isUndefined } from 'lodash';
import { memo } from 'react';

import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';

import { ProgressBar, type ProgressBarVariantsType } from '~/components/ProgressBar/ProgressBar';
import { STORAGE_METER_NUMBERS } from '~/constants/testIDs';

const MAX_PERCENT = 100;

export type WorkspaceStorageMeterProps = {
  percentOfStorageUsed?: number;
  showLabel?: boolean;
  showPercentage?: boolean;
  formattedUsedBytes?: string;
  formattedMaxBytes?: string;
  progressColor: ProgressBarVariantsType['color'];
};

export const WorkspaceStorageMeter = memo(
  ({
    percentOfStorageUsed,
    showLabel = false,
    showPercentage = false,
    formattedUsedBytes = '0 GB',
    formattedMaxBytes,
    progressColor,
  }: WorkspaceStorageMeterProps) => {
    if (isUndefined(percentOfStorageUsed)) {
      return null;
    }

    const fillPercent = Math.min(percentOfStorageUsed, MAX_PERCENT);

    return (
      <div className="flex flex-col items-start justify-start">
        {showLabel && (
          <div className="font-bold ads:text-metadata text-12 text-gray-9 uppercase tracking-wide">
            Workspace Storage
          </div>
        )}

        <div className="my-2 w-full">
          <ProgressBar size="small" color={progressColor} value={fillPercent} />
        </div>
        <div
          className="flex items-center gap-2 ads:text-metadata text-12 text-gray-9"
          data-testid={STORAGE_METER_NUMBERS}
        >
          <span>
            Using {formattedUsedBytes}
            {formattedMaxBytes && <> of {formattedMaxBytes}</>}
            {showPercentage && !!percentOfStorageUsed && ` (${percentOfStorageUsed}%)`}
          </span>
          <Tooltip className="max-w-[200px]" label="Please note that this total may take a few minutes to update.">
            <InfoIcon className="size-4" />
          </Tooltip>
        </div>
      </div>
    );
  },
);

WorkspaceStorageMeter.displayName = 'WorkspaceStorageMeter';
